import React from 'react';
import { useSlate } from 'slate-react';
import { useController } from '@aha-app/mvc';
import type {
  ComponentProps,
  EditorPlugin,
  ImageElement,
} from '../../../types/aha-editor';
import MediaCommon from '../MediaCommon';
import styles from './ImageComponentPlugin.module.css';
import Resizer from '../../../components/Resizer/Resizer';
import { type DragHandlePosition } from '../../../components/Resizer/DragHandle';
import {
  withImagePosition,
  ToolPositionInline,
  ToolPositionBlock,
} from '../tools/ToolPosition';
import ToolDelete from '../../../tools/ToolDelete';
import ToolImageViewer from '../tools/ToolImageViewer';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import ToolEditAlt from '../tools/ToolEditAlt';
import useDetectElementSize from './useDetectElementSize';
import tools from '../../../tools';
import AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import MissingImageIcon from './MissingImageIcon';
import withHoverToolbar from '../../../components/ElementToolbar/withHoverToolbar';

const Image: React.FC<ComponentProps<ImageElement>> = ({
  element,
  children,
}) => {
  const { media_src: src, media_position: position, media_alt: alt } = element;

  const editor = useSlate();
  const controller = useController(AhaEditorV2Controller);

  const { detectElementSize, width, height, originalWidth, originalHeight } =
    useDetectElementSize(element);

  let imageUrl: string | undefined;
  if (MediaCommon.isTemporaryDataUrl(src)) {
    imageUrl = MediaCommon.getTemporaryData(src);
  } else {
    imageUrl = src;
  }

  let handles: DragHandlePosition[] = [];
  if (position === 'inline') {
    handles = ['e'];
  } else {
    handles = ['w', 'e'];
  }

  return (
    <>
      <Resizer
        width={width}
        height={height}
        originalWidth={originalWidth}
        originalHeight={originalHeight}
        element={element}
        handles={handles}
        keepRatio
        showPixelSize
        liveResize
      >
        <SelectionRectangle>
          <span className={styles.image}>
            {imageUrl ? (
              <img
                src={imageUrl}
                width={Math.max(originalWidth ?? 0, width ?? 0)}
                height={Math.max(originalHeight ?? 0, height ?? 0)}
                alt={alt}
                ref={detectElementSize}
                onDoubleClick={(event) => {
                  tools.fireToolAction(ToolImageViewer, event, {
                    editor,
                    controller,
                  });
                }}
              />
            ) : (
              <MissingImageIcon
                width={originalWidth ?? 512}
                height={originalHeight ?? 512}
              />
            )}
          </span>
          {children}
        </SelectionRectangle>
      </Resizer>
    </>
  );
};

const ImageComponentPlugin: EditorPlugin = {
  name: 'Image component plugin',
  component: {
    type: 'image',
    component: withImagePosition(withHoverToolbar(Image, [ToolImageViewer])),
    toolbarItems: [
      [ToolImageViewer],
      [ToolPositionBlock, ToolPositionInline],
      [ToolEditAlt],
      [ToolDelete],
    ],
  },
};

export default ImageComponentPlugin;
